import React, { useMemo, useRef, useState } from "react";
import Sidebar from "../components/Layout/Sidebar";
import Header from "../components/Layout/Header";
import "../assets/styles/mainpage.css";
import { commonService } from "../services/otherServices";
import Toast from "../components/Common/Toast";
import CampaignStepper from "../components/Common/CampaignStepper";
import {
  Download,
  UploadCloud,
  FileText,
  Check,
  CheckCircle2,
  Ban,
  SlidersHorizontal,
  RotateCcw,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";

// Same normalization the backend's parse_keywords() uses (trim + lowercase,
// split on comma/newline, drop empties) - kept here since the frontend has
// no existing keyword-parsing helper to reuse.
const parseKeywordList = (raw: string): string[] => {
  if (!raw) return [];

  return raw
    .split(/[,\n]/)
    .map((keyword) => keyword.trim().toLowerCase())
    .filter((keyword) => keyword.length > 0);
};

// Strips a trailing "@domain" (or a bare trailing "@") from each keyword
// before it's saved. The backend only ever matches a keyword against the
// LOCAL PART of an email (the text before "@"), which by definition never
// contains "@" itself - so a keyword typed as "sales@" or pasted as a full
// address like "sales@company.com" can never match anything, silently
// rejecting every email found for that campaign. This has happened twice in
// production from someone typing/pasting keywords the natural "email-looking"
// way, so it's sanitized here rather than relying on everyone remembering
// the one correct format.
const sanitizeKeywordInput = (raw: string): string => {
  if (!raw) return raw;

  return raw
    .split(/([,\n])/) // keep delimiters so line breaks/commas survive
    .map((part) => (part === "," || part === "\n" ? part : part.replace(/@.*$/, "")))
    .join("");
};

const getConflictingKeywords = (includeRaw: string, excludeRaw: string): Set<string> => {
  const includeSet = new Set(parseKeywordList(includeRaw));
  const excludeSet = new Set(parseKeywordList(excludeRaw));
  const conflicts = new Set<string>();

  includeSet.forEach((keyword) => {
    if (excludeSet.has(keyword)) {
      conflicts.add(keyword);
    }
  });

  return conflicts;
};

// Re-renders the raw textarea text with any conflicting keyword segment
// wrapped in a red span, splitting on the same delimiters (comma/newline)
// used for parsing so the highlighted overlay lines up with what the user
// actually typed - kept as a single-element array member (no key) since
// plain strings don't need one.
const renderHighlightedKeywords = (raw: string, conflicts: Set<string>) => {
  if (!raw) return null;

  return raw.split(/([,\n])/).map((part, idx) => {
    if (part === "," || part === "\n") {
      return part;
    }

    const isConflict = part.trim() !== "" && conflicts.has(part.trim().toLowerCase());

    return isConflict ? (
      <span key={idx} className="keyword-conflict-text">
        {part}
      </span>
    ) : (
      part
    );
  });
};

const MainPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const includeTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const includeBackdropRef = useRef<HTMLDivElement | null>(null);
  const excludeTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const excludeBackdropRef = useRef<HTMLDivElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [includeKeywords, setIncludeKeywords] = useState("");
  const [excludeKeywords, setExcludeKeywords] = useState("");

  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Recomputed only when either field's text changes - a conflict is any
  // keyword (case-insensitive, trimmed) present in both Include and Exclude.
  const conflictingKeywords = useMemo(
    () => getConflictingKeywords(includeKeywords, excludeKeywords),
    [includeKeywords, excludeKeywords]
  );
  const hasKeywordConflict = conflictingKeywords.size > 0;

  const syncBackdropScroll = (backdropRef: React.RefObject<HTMLDivElement | null>) => (
    e: React.UIEvent<HTMLTextAreaElement>
  ) => {
    if (backdropRef.current) {
      backdropRef.current.scrollTop = e.currentTarget.scrollTop;
      backdropRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  // ✅ File select + validation
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const selectedFile = e.target.files[0];

    // ❌ Check CSV
    if (!selectedFile.name.endsWith(".csv")) {
      setToast({ type: "error", message: "Only CSV files are allowed" });
      return;
    }

    // ❌ Check empty file
    if (selectedFile.size === 0) {
      setToast({ type: "error", message: "File is empty" });
      return;
    }

    setFile(selectedFile);
  };

  const handleBoxClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileClear = () => {
    setFile(null);
    setProgress(0);
    setResult(null);
    setIncludeKeywords("");
    setExcludeKeywords("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }
  // ✅ Upload only on button click
  const handleFileUpload = async () => {
    if (!file) {
      setToast({ type: "error", message: "Please select a file first" });
      return;
    }

    try {
      setLoading(true);
      setProgress(0);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("include_keywords", sanitizeKeywordInput(includeKeywords));
      formData.append("exclude_keywords", sanitizeKeywordInput(excludeKeywords));

      const response = await commonService.upload(formData, (progress) => {
        setProgress(progress);
      });

      setResult(response.data?.data_count || 0);

      setToast({ type: "success", message: "File uploaded successfully" });

      setFile(null);
      setIncludeKeywords("");
      setExcludeKeywords("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      const timer = setTimeout(() => {
        setProgress(0);
        setResult(null);
      }, 15000);


    } catch (error:any) {
      const errorMessage =
        error?.response?.data?.detail ||   // FastAPI HTTPException
        error?.response?.data?.message ||  // custom backend message
        error?.message ||                  // axios message
        "Upload failed";

      setToast({
        type: "error",
        message: errorMessage,
      });
      // console.error(error);
      // setToast({ type: "error", message: "Upload failed" });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ["company_name", "company_domain"];

    // Create CSV content
    const csvContent = headers.join(",") + "\n";

    // Create blob
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    // Create download link
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    const uniqueFileName = `campaign_template_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 8)}.csv`;

    link.setAttribute("download", uniqueFileName);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  return (
    <div className="app-container">
      <Sidebar />

      <div className="main">
        <Header />

        <div className="content">
          <CampaignStepper currentStep={1} />

          <div className="audience-page">
            <div className="audience-page-header">
              <div>
                <h2 className="audience-page-title">Step 1: Upload Your Target Domains</h2>
                <p className="audience-page-subtitle">
                  Upload your list of company domains and set keyword filters for email discovery.
                </p>
              </div>
              <button type="button" className="btn-download-template" onClick={downloadTemplate}>
                <Download size={16} />
                Download Template
              </button>
            </div>

            {/* Hidden input */}
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {/* ✅ CSV upload card */}
            <div className="audience-card">
              <div className="csv-upload-section">
                <div className="csv-upload-left" onClick={handleBoxClick}>
                  <UploadCloud size={40} className="upload-box-icon" />
                  <div className="upload-box-title">Upload CSV File</div>
                  <p className="upload-box-subtext">
                    {file ? file.name : "Drag & drop your CSV file here or"}
                  </p>
                  <button
                    type="button"
                    className="btn-browse-files"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBoxClick();
                    }}
                  >
                    <UploadCloud size={14} />
                    Browse Files
                  </button>
                  <span className="upload-box-hint">Only .csv files allowed</span>
                </div>

                <div className="csv-requirements-card">
                  <div className="csv-requirements-title">
                    <FileText size={16} />
                    CSV Requirements
                  </div>
                  <ul className="csv-requirements-list">
                    <li>
                      <Check size={14} />
                      Must contain <code>company_domain</code> column
                    </li>
                    <li>
                      <Check size={14} />
                      One domain per row
                    </li>
                    <li>
                      <Check size={14} />
                      Maximum file size: 10MB
                    </li>
                  </ul>
                  <p className="csv-requirements-help">
                    Need help?{" "}
                    <span className="link" onClick={downloadTemplate}>
                      Download our template.
                    </span>
                  </p>
                </div>
              </div>

              {/* ✅ Show progress only when uploading */}
              {loading && (
                <>
                  <div className="progress-section">
                    <span>Uploading & Processing...</span>
                    <span>{progress}% Complete</span>
                  </div>

                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </>
              )}
            </div>

            {/* ✅ Keyword Filters card */}
            <div className="audience-card">
              <div className="keyword-filters-header">
                <div>
                  <div className="keyword-filters-title">
                    <SlidersHorizontal size={16} />
                    Keyword Filters <span className="optional-text">(Optional)</span>
                  </div>
                  <p className="keyword-filters-subtitle">
                    Define keywords to include or exclude in discovered email addresses.
                  </p>
                </div>
                <span className="optional-badge">Optional</span>
              </div>

              <div className="keyword-cards-row">
                <div className="keyword-card keyword-card--include">
                  <div className="keyword-card-header">
                    <CheckCircle2 size={16} />
                    Include Keywords
                  </div>
                  <p className="keyword-card-desc">
                    Only emails containing these keywords will be included.
                  </p>
                  <div className="keyword-textarea-wrap">
                    <div
                      className="keyword-highlight-backdrop"
                      ref={includeBackdropRef}
                      aria-hidden="true"
                    >
                      {renderHighlightedKeywords(includeKeywords, conflictingKeywords)}
                    </div>
                    <textarea
                      ref={includeTextareaRef}
                      className={`keyword-textarea${hasKeywordConflict ? " keyword-textarea--conflict" : ""}`}
                      value={includeKeywords}
                      onChange={(e) => setIncludeKeywords(e.target.value)}
                      onScroll={syncBackdropScroll(includeBackdropRef)}
                      placeholder={"Enter keywords separated by comma or newline\ne.g. sales, contact, info"}
                      rows={4}
                    />
                  </div>
                  <div className="keyword-card-footer">
                    <span className="keyword-helper-text">At least one keyword to include (recommended)</span>
                  </div>
                </div>

                <div className="keyword-card keyword-card--exclude">
                  <div className="keyword-card-header">
                    <Ban size={16} />
                    Exclude Keywords
                  </div>
                  <p className="keyword-card-desc">
                    Emails containing these keywords will be excluded.
                  </p>
                  <div className="keyword-textarea-wrap">
                    <div
                      className="keyword-highlight-backdrop"
                      ref={excludeBackdropRef}
                      aria-hidden="true"
                    >
                      {renderHighlightedKeywords(excludeKeywords, conflictingKeywords)}
                    </div>
                    <textarea
                      ref={excludeTextareaRef}
                      className={`keyword-textarea${hasKeywordConflict ? " keyword-textarea--conflict" : ""}`}
                      value={excludeKeywords}
                      onChange={(e) => setExcludeKeywords(e.target.value)}
                      onScroll={syncBackdropScroll(excludeBackdropRef)}
                      placeholder={"Enter keywords separated by comma or newline\ne.g. admin, support, test"}
                      rows={4}
                    />
                  </div>
                  <div className="keyword-card-footer">
                    <span className="keyword-helper-text">Helps remove generic or unwanted emails</span>
                  </div>
                </div>
              </div>

              {hasKeywordConflict && (
                <div className="keyword-conflict-banner" role="alert">
                  <AlertTriangle size={14} />
                  Same keywords cannot be used in both Include and Exclude. Please remove the same keyword from either field before continuing.
                </div>
              )}
            </div>

            {/* ✅ Bottom actions */}
            <div className="audience-bottom-actions">
              <button
                type="button"
                className="btn-clear-all"
                onClick={handleFileClear}
                disabled={(!file && !includeKeywords && !excludeKeywords) || loading}
              >
                <RotateCcw size={14} />
                Clear All
              </button>
              <button
                type="button"
                className="btn-upload-continue"
                onClick={handleFileUpload}
                disabled={!file || loading || hasKeywordConflict}
                title={hasKeywordConflict ? "Resolve the Include/Exclude keyword conflict before continuing" : undefined}
              >
                {loading ? "Uploading..." : "Upload & Continue"}
                <ArrowRight size={16} />
              </button>
            </div>

            <span className="audience-bottom-note">
              <i>Note: company_domain column is mandatory.</i>
            </span>
          </div>
        </div>
      </div>


      {/* ✅ Toast */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default MainPage;